import { Tooltip } from '@ttab/elephant-ui'
import { cn } from '@ttab/elephant-ui/utils'
import { useTranslation } from 'react-i18next'
import { HistoryIcon, historyColors } from './HistoryIcon'
import { MoveRightIcon } from '@ttab/elephant-ui/icons'

export const HistoryEntry = ({
  version,
  isCurrent = false,
  status,
  isLast,
  title,
  time,
  onSelect,
  selected = false,
  label,
  cause,
  author,
  publishVersion,
  selectOn = 'mousedown'
}: {
  version?: bigint
  isCurrent?: boolean
  status: string | null
  isLast?: boolean
  title?: string | null
  time?: string
  onSelect?: (version: bigint) => void
  selected?: boolean
  label?: string
  cause?: string
  author?: string
  publishVersion?: bigint
  // Use 'click' when selecting opens a modal, else the mouseup dismisses it as an outside click
  selectOn?: 'mousedown' | 'click'
}) => {
  const { t } = useTranslation('wires')
  const isSelectable = !!version && !selected && !!onSelect

  const select = () => {
    if (isSelectable) {
      onSelect(version)
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()

    if (selectOn === 'mousedown') {
      select()
    }
  }

  const handleClick = () => {
    if (selectOn === 'click') {
      select()
    }
  }

  return (
    <>
      <div
        className={cn(
          'relative flex items-start justify-center pe-2 h-full',
          isSelectable && 'cursor-pointer group-hover:bg-muted/60'
        )}
        onMouseDownCapture={handleMouseDown}
        onClick={handleClick}
      >
        <HistoryIcon
          status={status || 'draft'}
          isCurrent={isCurrent}
          isLast={isLast}
        />
      </div>

      <div
        className={cn(
          'py-1 ps-2 flex items-start gap-1 tabular-nums',
          isSelectable ? 'cursor-pointer group-hover:bg-muted/60' : 'cursor-default',
          !title && 'text-muted-foreground',
          selected && 'font-semibold'
        )}
        onMouseDownCapture={handleMouseDown}
        onClick={handleClick}
      >
        {selected && <MoveRightIcon size={12} className='shrink-0 -ml-4 mt-1' strokeWidth={3} color={historyColors[status ?? 'draft']} />}
        {time}
      </div>

      <a
        className={cn(
          'py-1 ps-3 items-center',
          author === undefined && 'truncate',
          isSelectable ? 'cursor-pointer group-hover:bg-muted/60' : 'cursor-default',
          selected && 'font-semibold'
        )}
        onMouseDownCapture={handleMouseDown}
        onClick={handleClick}
      >
        {author !== undefined && (
          <span className='flex flex-col min-w-0'>
            <span className='flex flex-wrap gap-x-1'>
              <span className='text-foreground'>{label}</span>
              {cause && <span>{`- ${cause}`}</span>}
              {publishVersion !== undefined && <span>{`- v${publishVersion}`}</span>}
            </span>

            <span className='break-words'>{author}</span>
          </span>
        )}

        {author === undefined && ((title && version)
          ? (
              <Tooltip content={(
                <div className='flex flex-col gap-2'>
                  <span className='font-semibold'>
                    {t('preview.version')}
                    {` `}
                    {version}
                  </span>

                  <span>
                    {title}
                  </span>
                </div>
              )}
              >
                {title}
              </Tooltip>
            )
          : (
              <span className={cn(
                'text-muted-foreground',
                selected && ' opacity-70'
              )}
              >
                {historyEntryTitle(status, t)}
              </span>
            ))}
      </a>
    </>
  )
}


const historyEntryTitle = (status: string | null, t: (key: string) => string): string => {
  const statuses: Record<string, string> = {
    draft: '',
    done: t('history.status.done'),
    saved: t('history.status.saved'),
    approved: t('history.status.approved'),
    read: t('history.status.read'),
    usable: t('history.status.usable'),
    used: t('history.status.used'),
    withheld: t('history.status.withheld'),
    cancelled: t('history.status.cancelled'),
    unpublished: t('history.status.unpublished'),
    flash: t('history.status.flash'),
    system: t('history.status.system')
  }
  return statuses[status ?? ''] || ''
}
